/**
 * Local-calendar helpers for the meal-plan screens.
 *
 * Plans carry a plain `date` (YYYY-MM-DD) and a wall-clock `serveAt` (HH:MM) in
 * the household's timezone, while the timeline speaks full ISO datetimes.
 * Everything here works in the browser's local calendar — never `toISOString()`
 * on a date-only value, which would shift the day west of UTC.
 */

const MS_PER_DAY = 86_400_000;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** A `Date` → local `YYYY-MM-DD`. */
export function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** `YYYY-MM-DD` → local midnight `Date`. Invalid input yields an invalid Date. */
export function parseIsoDate(iso: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return new Date(NaN);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

export function isIsoDate(value: string | null | undefined): value is string {
  return typeof value === 'string' && !Number.isNaN(parseIsoDate(value).getTime());
}

/** Today in the local calendar. */
export function todayIso(now: Date = new Date()): string {
  return toIsoDate(now);
}

/** `YYYY-MM-DD` shifted by whole days (DST-safe: it walks the local calendar). */
export function addDays(iso: string, days: number): string {
  const d = parseIsoDate(iso);
  d.setDate(d.getDate() + days);
  return toIsoDate(d);
}

/** Whole days from `a` to `b` (b − a) in the local calendar. */
export function daysBetween(a: string, b: string): number {
  const from = parseIsoDate(a);
  const to = parseIsoDate(b);
  return Math.round((to.getTime() - from.getTime()) / MS_PER_DAY);
}

/** The Monday on or before `iso` (weeks run Mon–Sun). */
export function startOfWeek(iso: string): string {
  const d = parseIsoDate(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // getDay(): 0 = Sunday … 6 = Saturday.
  const backwards = (d.getDay() + 6) % 7;
  return addDays(iso, -backwards);
}

/** Inclusive `{ from, to }` for the Mon–Sun week containing `iso` — the list query. */
export function weekRange(iso: string): { from: string; to: string } {
  const from = startOfWeek(iso);
  return { from, to: addDays(from, 6) };
}

/** The seven dates of the week containing `iso`, Monday first. */
export function weekDays(iso: string): string[] {
  const from = startOfWeek(iso);
  return Array.from({ length: 7 }, (_, i) => addDays(from, i));
}

export function isToday(iso: string, now: Date = new Date()): boolean {
  return iso === todayIso(now);
}

// -------------------------------------------------------------------- month --

/** `YYYY-MM-DD` or `YYYY-MM` → the first of that local-calendar month. */
export function startOfMonth(iso: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[1]}-${m[2]}-01`;
}

/** The first-of-month `days` months away (negative walks backwards). */
export function addMonths(iso: string, months: number): string {
  const d = parseIsoDate(startOfMonth(iso));
  if (Number.isNaN(d.getTime())) return iso;
  d.setDate(1); // pin the day before shifting months, so e.g. Jan 31 + 1 month can't skid to March
  d.setMonth(d.getMonth() + months);
  return toIsoDate(d);
}

/**
 * The Mon–Sun calendar grid covering the month containing `iso`: every day
 * from the Monday on/before the 1st through the Sunday on/after the last day,
 * so full weeks are always shown (35 or 42 days).
 */
export function monthGridDays(iso: string): string[] {
  const first = startOfMonth(iso);
  const gridStart = startOfWeek(first);
  const nextMonth = addMonths(first, 1);
  const last = addDays(nextMonth, -1);
  const gridEnd = addDays(startOfWeek(last), 6);
  const days: string[] = [];
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) days.push(d);
  return days;
}

/** Inclusive `{ from, to }` for the calendar grid — the list query for month view. */
export function monthGridRange(iso: string): { from: string; to: string } {
  const days = monthGridDays(iso);
  return { from: days[0]!, to: days[days.length - 1]! };
}

/** Whether `iso` falls in the same local-calendar month as `monthIso`. */
export function isSameMonth(iso: string, monthIso: string): boolean {
  return iso.slice(0, 7) === startOfMonth(monthIso).slice(0, 7);
}

/** "August 2026" — the month grid's heading. */
export function formatMonthLabel(iso: string): string {
  const d = parseIsoDate(startOfMonth(iso));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

// --------------------------------------------------------------- serve time --

/** `HH:MM` guard for the plan's `serveAt` field. */
export function isTimeOfDay(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}

/**
 * Plan `date` + wall-clock `HH:MM` → the full local ISO datetime the timeline
 * endpoint expects. Returns undefined when either half is missing or malformed.
 */
export function combineDateTime(date: string, time: string | null | undefined): string | undefined {
  if (!isIsoDate(date) || !isTimeOfDay(time)) return undefined;
  const [h, min] = time.split(':').map(Number) as [number, number];
  const d = parseIsoDate(date);
  d.setHours(h, min, 0, 0);
  return d.toISOString();
}

/** Offset (minutes, local minus UTC) of `timeZone` at the instant `at`. */
function zoneOffsetMinutes(timeZone: string, at: Date): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts: Record<string, string> = {};
  for (const part of dtf.formatToParts(at)) parts[part.type] = part.value;
  const asUtc = Date.UTC(
    Number(parts['year']),
    Number(parts['month']) - 1,
    Number(parts['day']),
    Number(parts['hour']),
    Number(parts['minute']),
    Number(parts['second']),
  );
  return (asUtc - at.getTime()) / 60_000;
}

/**
 * Plan `date` + wall-clock `HH:MM` in the household's `timeZone` → the UTC ISO
 * instant, the same conversion the API applies to a saved serve time
 * (`apps/api/src/routes/plans/time.ts`), so a previewed timeline matches the
 * one the server builds once the plan is saved. Falls back to the browser's
 * zone (`combineDateTime`) when the zone name is unknown.
 */
export function zonedDateTimeIso(
  date: string,
  time: string | null | undefined,
  timeZone: string | null | undefined,
): string | undefined {
  if (!isIsoDate(date) || !isTimeOfDay(time)) return undefined;
  if (!timeZone) return combineDateTime(date, time);
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const [hh, mm] = time.split(':').map(Number) as [number, number];
  const naiveUtc = Date.UTC(y, m - 1, d, hh, mm, 0);
  try {
    const offset = zoneOffsetMinutes(timeZone, new Date(naiveUtc));
    return new Date(naiveUtc - offset * 60_000).toISOString();
  } catch {
    return combineDateTime(date, time);
  }
}

/** An ISO datetime → the local `HH:MM` wall clock, for `<input type="time">`. */
export function timeOfDayFromIso(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Whole minutes from `a` to `b`, rounded; negative when `b` is earlier. */
export function minutesBetween(a: string, b: string): number {
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 60_000);
}

// ----------------------------------------------------------------- display --

/** "17:10" / "5:10 PM" — whichever the browser's locale uses. */
export function formatClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** "17:10–17:25" for a timeline entry. */
export function formatClockRange(startIso: string, endIso: string): string {
  const start = formatClock(startIso);
  const end = formatClock(endIso);
  return end && end !== start ? `${start}–${end}` : start;
}

/** "Mon 24" — the week grid's column heading. */
export function formatDayShort(iso: string): string {
  const d = parseIsoDate(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' });
}

/** "Monday, 24 August" — the day heading on phones and the plan editor. */
export function formatDayLong(iso: string): string {
  const d = parseIsoDate(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}

/** "24–30 Aug 2026" (or with both months when the week straddles one). */
export function formatWeekLabel(from: string, to: string): string {
  const a = parseIsoDate(from);
  const b = parseIsoDate(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return `${from} – ${to}`;
  const sameMonth = a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  const left = sameMonth
    ? a.toLocaleDateString(undefined, { day: 'numeric' })
    : a.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  const right = b.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return `${left} – ${right}`;
}

/** "1 hr 20 min" / "25 min" / "" — shared by the timeline header and step cards. */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '';
  const total = Math.round(minutes);
  if (total <= 0) return '';
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}
